import { Link } from "react-router-dom";
import { Flag, Medal, PaperPlaneTilt, Scales, ShareNetwork, UsersThree } from "@phosphor-icons/react";
import { Panel } from "@/components/admin/AdminUI";
import GenderSplit from "@/components/admin/launch/GenderSplit";
import MiniBars from "@/components/admin/MiniBars";
import SiteTiles from "@/components/admin/SiteTiles";

const sum = (days, f) => (days || []).reduce((n, d) => n + (d[f] || 0), 0);

function Card({ icon: Icon, title, value, sub, to, testId, children }) {
  const body = (
    <>
      <div className="flex items-center gap-2"><Icon size={18} className="cw-brand-text" aria-hidden /><p className="cw-soft text-sm">{title}</p></div>
      <p className="mt-3 text-3xl font-semibold tabular-nums tracking-tight" data-testid={`${testId}-value`}>{value}</p>
      {sub && <p className="cw-soft mt-1 text-sm" data-testid={`${testId}-sub`}>{sub}</p>}
      {children && <div className="mt-4">{children}</div>}
    </>
  );
  return to ? <Link to={to} className="block rounded-[20px] border border-border bg-white p-6 transition-shadow hover:shadow-md" data-testid={testId}>{body}</Link>
    : <Panel className="p-6" data-testid={testId}>{body}</Panel>;
}

function TestBlock({ t }) {
  return (
    <Panel className="bg-[var(--cw-surface)] p-6 opacity-80" data-testid="dashboard-test-data">
      <p className="cw-muted text-xs font-medium uppercase tracking-wide">Test data, not counted above</p>
      <div className="cw-muted mt-3 grid gap-6 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {t.waitlist && <div data-testid="dashboard-test-waitlist"><p>{t.waitlist.total} test waitlist addresses</p>
          {t.waitlist.total > 0 && <div className="mt-2 opacity-70 grayscale"><GenderSplit items={t.waitlist.by_gender} testId="dashboard-test-waitlist-split" /></div>}</div>}
        {t.waves && <p data-testid="dashboard-test-waves">{t.waves.sent} test waves sent · {t.waves.opened} opened · {t.waves.draft} draft</p>}
        {t.referrals && <p data-testid="dashboard-test-referrals">{t.referrals.codes_used} test referrals used · {t.referrals.codes_issued} issued · {t.referrals.rewards} rewarded</p>}
        <p data-testid="dashboard-test-users">{t.users} test accounts · {t.reports_open} reports · {t.appeals_open} appeals open</p>
        <div><p>{sum(t.daily, "signups")} signups (14 days)</p><MiniBars days={t.daily} field="signups" muted testId="dashboard-test-signups-chart" /></div>
        <div><p>{sum(t.daily, "matches")} matches (14 days)</p><MiniBars days={t.daily} field="matches" muted testId="dashboard-test-matches-chart" /></div>
      </div>
      {t.site && <div className="mt-6"><p className="cw-muted mb-3 text-xs font-medium">Website (preview, localhost and staging visits)</p><SiteTiles s={t.site} muted testId="dashboard-test-site" /></div>}
    </Panel>
  );
}

export default function LaunchTiles({ d }) {
  return (
    <div className="space-y-5" data-testid="dashboard-launch-tiles">
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <Card icon={UsersThree} title="Waitlist" value={d.waitlist.total} to="/admin/launch?tab=waitlist" testId="dashboard-waitlist">
          <GenderSplit items={d.waitlist.by_gender} testId="dashboard-waitlist-split" />
        </Card>
        <Card icon={PaperPlaneTilt} title="Waves sent" value={d.waves.sent} sub={`${d.waves.opened} opened · ${d.waves.draft} draft`} to="/admin/launch?tab=waves" testId="dashboard-waves" />
        <Card icon={Medal} title="Founding Crush" value={`${d.founding.count} / ${d.founding.limit}`} to="/admin/launch?tab=founding" testId="dashboard-founding" />
        <Card icon={ShareNetwork} title="Referrals used" value={d.referrals.codes_used} sub={`${d.referrals.codes_issued} issued · ${d.referrals.rewards} rewarded`} to="/admin/launch?tab=referrals" testId="dashboard-referrals" />
      </div>
      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        <Card icon={UsersThree} title="Signups, last 14 days" value={sum(d.daily, "signups")} testId="dashboard-signups"><MiniBars days={d.daily} field="signups" testId="dashboard-signups-chart" /></Card>
        <Card icon={ShareNetwork} title="Matches, last 14 days" value={sum(d.daily, "matches")} testId="dashboard-matches"><MiniBars days={d.daily} field="matches" testId="dashboard-matches-chart" /></Card>
        <Card icon={Flag} title="Reports open" value={d.reports_open} to="/admin/reports" testId="dashboard-reports-open" />
        <Card icon={Scales} title="Appeals open" value={d.appeals_open} to="/admin/appeals" testId="dashboard-appeals-open" />
      </div>
      {d.site && <><h2 className="pt-5 text-lg font-semibold">Website</h2><SiteTiles s={d.site} /></>}
      {d.test && <TestBlock t={d.test} />}
    </div>
  );
}
